// app/(app)/employees/index.tsx
import { Button, Input } from "@/components/default/forms";
import EmployeeCard from "@/components/employee/EmployeeCard";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, fetchEmployees } from "@/store/slices/employeeSlice";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function EmployeesListScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { employees, pagination, loading, error } = useAppSelector(
    (state) => state.employees,
  );

  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // Load initial data
  useFocusEffect(
    useCallback(() => {
      if (error) {
        // Optionally show toast/alert
        dispatch(clearError());
      }
      dispatch(fetchEmployees({ page: 1, limit, search: searchText }));
    }, [searchText]),
  );

  const loadMore = () => {
    if (!loading && pagination.hasNextPage) {
      const nextPage = pagination.currentPage + 1;
      setCurrentPage(nextPage);
      dispatch(fetchEmployees({ page: nextPage, limit, search: searchText }));
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    dispatch(fetchEmployees({ page: 1, limit, search: searchText })).finally(
      () => setRefreshing(false),
    );
  };

  const handleSearch = () => {
    // Search is already triggered by useEffect when searchText changes
    setCurrentPage(1);
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: spacing.lg,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: { fontSize: 24, fontWeight: "600", color: colors.text },
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    searchInput: { flex: 1 },
    listContent: { paddingBottom: spacing.xl },
    emptyContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: spacing.xxl,
    },
    emptyText: { fontSize: 16, color: colors.textLight },
    footerLoader: { paddingVertical: spacing.lg, alignItems: "center" },
  });

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Employees</Text>
        <Button
          title="+ Add"
          onPress={() => router.push("/(app)/employees/add")}
          variant="primary"
          containerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}
        />
      </View>
      <View style={styles.searchContainer}>
        <Input
          placeholder="Search by name or email"
          value={searchText}
          onChangeText={setSearchText}
          onSubmitEditing={handleSearch}
          containerStyle={styles.searchInput}
        />
        <Button
          title="Search"
          onPress={handleSearch}
          variant="outline"
          containerStyle={{ paddingHorizontal: 12 }}
        />
      </View>
      <FlatList
        data={employees}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => (
          <EmployeeCard employee={item} onRefresh={handleRefresh} />
        )}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListEmptyComponent={
          !loading ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No employees found</Text>
            </View>
          ) : null
        }
        ListFooterComponent={
          loading && employees.length > 0 ? (
            <View style={styles.footerLoader}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          ) : null
        }
      />
    </View>
  );
}
